import React, {memo, useCallback, useState} from "react"
import {debounce} from "@mui/material/utils"
import {useTemplatedComponent} from "@next/templating"
import {MANIFEST} from "@manifest/plp"
import {CurrencyConfiguration} from "@next/utils"
import {TextModel} from "models/Text"
import connect from "./connect"
import {PRICE_SLIDER_DELAY} from "../../config/constants"
import {useIsOnClient} from "../../hooks/useIsOnClient"

interface FiltersFeatsProps {
    name: string
    isTabbedFilter?: boolean
    min: number
    max: number
    currentSelectedValues: number[]
    locale: string
    realm: string
    currencyCode: string
    updatePriceFilters: ({
        name,
        isTabbedFilter,
        selectedMin,
        selectedMax,
    }: {
        name: string
        isTabbedFilter: boolean
        selectedMin: number
        selectedMax: number
    }) => void
    text: TextModel
    horizontalPadding?: string
    topPadding?: string
    globalPriceFormattingConfig: CurrencyConfiguration
    templating: {mainTemplate: string; fallbackTemplate: string}
}

export const FacetsPrice = memo(
    ({
        name,
        isTabbedFilter = false,
        min,
        max,
        currentSelectedValues,
        updatePriceFilters,
        locale,
        realm,
        currencyCode,
        text,
        globalPriceFormattingConfig,
        templating,
    }: FiltersFeatsProps) => {
        const {isOnClient} = useIsOnClient()
        const [value, setValue] = useState<number[]>(currentSelectedValues)
        const Component = useTemplatedComponent(templating, MANIFEST)

        const handleChange = (_event: any, newValue: number | number[]) => {
            setValue(newValue as number[])
        }

        const debounceUpdatePriceFilters = debounce((_event: any, newValue: number | number[]) => {
            updatePriceFilters({
                name,
                isTabbedFilter,
                selectedMin: newValue[0],
                selectedMax: newValue[1],
            })
        }, PRICE_SLIDER_DELAY)

        const handleChangeCommitted = useCallback((event, newValue) => {
            if (event.type === "mouseup" || event.type === "touchend") {
                updatePriceFilters({
                    name,
                    isTabbedFilter,
                    selectedMin: newValue[0],
                    selectedMax: newValue[1],
                })
            } else {
                debounceUpdatePriceFilters(event, newValue)
            }
        }, [])

        if (!isOnClient) return null

        return (
            <Component
                min={min}
                max={max}
                value={value}
                onChange={handleChange}
                onChangeCommitted={handleChangeCommitted}
                locale={locale}
                realm={realm}
                currencyCode={currencyCode}
                text={text}
                globalPriceFormattingConfig={globalPriceFormattingConfig}
            />
        )
    },
)
FacetsPrice.displayName = "FacetsPrice"

export default connect(FacetsPrice)
